import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import { ReactNode } from 'react';
import { type VariantProps, tv } from 'tailwind-variants';

import ChevronRightIcon from '@cartwhl/ui/icons/ChevronRightIcon';

import Text from '../base/Text';
import './Dropdown.css';
import Tooltip from './Tooltip';

const DEFAULT_DROPDOWN_Z_INDEX = 20;
const MODAL_DROPDOWN_Z_INDEX = 10000;

export default function Dropdown({
  trigger,
  children,
  open,
  onOpenChange,
  side,
  align = 'end',
  disabled,
  width,
  inModal = false,
  zIndex,
  sideOffset = 8,
  triggerTooltip,
  triggerTooltipSide = 'top',
}: {
  trigger: ReactNode;
  children: ReactNode;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  side?: 'top' | 'right' | 'bottom' | 'left';
  align?: 'start' | 'center' | 'end';
  disabled?: boolean;
  width?: number;
  inModal?: boolean;
  zIndex?: number;
  sideOffset?: number;
  triggerTooltip?: ReactNode;
  triggerTooltipSide?: 'top' | 'right' | 'bottom' | 'left';
}) {
  const dropdownZIndex =
    zIndex ?? (inModal ? MODAL_DROPDOWN_Z_INDEX : DEFAULT_DROPDOWN_Z_INDEX);

  const triggerNode = (
    <DropdownMenu.Trigger asChild disabled={disabled}>
      {trigger}
    </DropdownMenu.Trigger>
  );

  return (
    <DropdownMenu.Root open={open} onOpenChange={onOpenChange} modal={false}>
      {triggerTooltip != null ? (
        <Tooltip
          text={triggerTooltip}
          side={triggerTooltipSide}
          inModal={inModal}
          disabled={disabled}
        >
          {triggerNode}
        </Tooltip>
      ) : (
        triggerNode
      )}
      <DropdownMenu.Portal>
        <DropdownMenu.Content
          side={side}
          className="RxDropdownContent text-gray-90 dark:bg-gray-75 shadow-menu min-w-fit rounded-2xl bg-white p-2 dark:text-white"
          align={align}
          collisionPadding={8}
          sideOffset={sideOffset}
          style={{ width, zIndex: dropdownZIndex }}
        >
          <Text as="div" variant="studio/body/medium/sm">
            {children}
          </Text>
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
}

const itemInner = tv({
  base: 'group flex w-full items-center rounded-lg py-2.5 pl-3 pr-3 transition-colors dark:text-white',
  variants: {
    variant: {
      default: '',
      blue: 'bg-blue/10',
    },
    disabled: {
      true: 'cursor-default opacity-50',
      false: 'cursor-pointer',
    },
    justify: {
      start: 'justify-start',
      center: 'justify-center',
      end: 'justify-end',
    },
  },
  compoundVariants: [
    {
      variant: 'default',
      disabled: false,
      className: 'hover:bg-gray-15 dark:hover:bg-gray-90',
    },
    {
      variant: 'blue',
      disabled: false,
      className:
        'hover:bg-blue dark:hover:bg-blue hover:text-white dark:hover:text-white',
    },
  ],
  defaultVariants: {
    variant: 'default',
    disabled: false,
  },
});
Dropdown.Item = function Item({
  children,
  href,
  onClick,
  icon,
  disabled = false,
  variant,
  className,
  itemClassName,
  textClassName,
  justify,
}: {
  children: ReactNode;
  href?: string;
  onClick?: () => void;
  icon?: ReactNode;
  disabled?: boolean;
  variant?: VariantProps<typeof itemInner>['variant'];
  justify?: VariantProps<typeof itemInner>['justify'];
  className?: string;
  itemClassName?: string;
  textClassName?: string;
}) {
  const Element = href ? 'a' : 'button';
  const path = href?.split('?')[0];
  const isExternal = path && !path.includes('.getcartwheel.com');

  return (
    <DropdownMenu.Item
      className={`outline-hidden overflow-hidden rounded-lg ${className}`}
      onClick={(e) => e.stopPropagation()}
      disabled={disabled}
    >
      <Element
        href={href}
        target={isExternal ? '_blank' : undefined}
        rel={isExternal ? 'noreferrer noopener' : undefined}
        onClick={onClick}
        className={itemInner({ variant, disabled, justify, className: itemClassName })}
      >
        {icon !== undefined && <span className="mr-3 w-5">{icon}</span>}
        <Text variant="studio/body/medium/sm" className={textClassName}>
          {children}
        </Text>
      </Element>
    </DropdownMenu.Item>
  );
};

Dropdown.Sub = function Sub({
  children,
  text,
  icon,
  suffix,
  zIndex,
  contentClassName,
  textClassName,
}: {
  children: ReactNode;
  text: string;
  icon?: ReactNode | null | undefined; // Null means: empty icon. Undefined means no icon.
  /** Rendered between the label and the chevron (e.g. a “New” pill). */
  suffix?: ReactNode;
  zIndex?: number;
  /** Classes for the inner flyout body (width, padding, etc.). */
  contentClassName?: string;
  /** Classes for the row label text only. */
  textClassName?: string;
}) {
  return (
    <DropdownMenu.Sub>
      <DropdownMenu.SubTrigger className="dark:hover:bg-gray-90 hover:bg-gray-15 data-[state=open]:bg-gray-15 dark:data-[state=open]:bg-gray-90 outline-hidden flex min-w-0 w-[260px] cursor-default items-center gap-3 rounded-lg px-4 py-2.5 transition-colors">
        {icon !== undefined && <span className="mr-0 flex w-5 shrink-0 justify-center">{icon}</span>}
        <Text
          variant="studio/body/medium/sm"
          className={`min-w-0 flex-1 truncate text-left ${textClassName ?? ''}`}
        >
          {text}
        </Text>
        {suffix != null ? (
          <span className="flex shrink-0 items-center">{suffix}</span>
        ) : null}
        <ChevronRightIcon className="text-gray-45 shrink-0" />
      </DropdownMenu.SubTrigger>
      <DropdownMenu.Portal>
        <DropdownMenu.SubContent
          className="RxSubContent text-gray-90 dark:bg-gray-75 shadow-menu mr-[-150px] rounded-2xl bg-white p-2 sm:mr-0 dark:text-white"
          style={{ zIndex: zIndex ?? DEFAULT_DROPDOWN_Z_INDEX }}
        >
          <div
            className={
              contentClassName ??
              'flex min-w-0 flex-col gap-0 text-[14px]/[18px] font-medium'
            }
          >
            {children}
          </div>
        </DropdownMenu.SubContent>
      </DropdownMenu.Portal>
    </DropdownMenu.Sub>
  );
};

Dropdown.Separator = function Separator() {
  return (
    <DropdownMenu.Separator className="bg-gray-30 dark:bg-gray-60 -mx-2 my-2 h-px" />
  );
};
